/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * What Nhóm máy chủ and the plan tab "Kết nối module" share (ADR-032 §2.4–2.6, §2.11): the
 * connections of a branch, what each member's connection reports (status, accounts / capacity,
 * age of its snapshot), a value read from every accepting member (values missing on one of them
 * greyed), the placement settings of a module, and "Làm mới".
 */
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import type { CatalogPlan, PlacementMember } from '../../api/areas/catalog';
import type { HostingServer } from '../../api/areas/hosting';
import type { ModuleBranchId, ModuleField, ModuleManifest, ModuleSummary } from '../../api/areas/modules';
import { Badge } from '../../components/Badge';
import { DataTable, Field, type Column } from '../../components/blocks';
import { WriteError } from '../../components/Write';
import { commonChoices } from '../../lib/catalog';
import { formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { choicesOf, FieldInput } from '../modules/SchemaForm';
import { checkValue, initialValue, isBlank, isRequired, isWriteOnly, payloadValue, type FormValue } from '../modules/fieldKinds';
import { InstanceStatusBadge, modulesUrl, useInstanceLabel, useText, useValueText } from '../modules/ui';
import { selectClass } from '../operator/shared';
import { useCatalogT } from './editorKit';

/**
 * The modules of a branch with their connections (Cài đặt › Module; `integration.read`):
 * `undefined` while loading, `null` when they cannot be read (no `integration.read`, no branch).
 */
export function useModuleSummaries(branch: ModuleBranchId | null): ModuleSummary[] | null | undefined {
  const r = useApi((signal) => (branch ? api.getModuleBranch(branch, { signal }) : Promise.resolve(null)), [branch]);
  if (r.status === 'loading') return undefined;
  return r.status === 'success' && r.data ? r.data.branch.modules : null;
}

/**
 * The module each plan of a product is delivered through (its module connection), as a manifest:
 * their option types and overridable placement settings are offered in its configurable options
 * (ADR-032 §2.7). Plan id → manifest, `null` for a module the branch does not list; a plan without
 * a module connection is absent. `null` while loading or without `integration.read`.
 */
export function usePlanModules(branch: ModuleBranchId | null, plans: readonly CatalogPlan[]): Map<string, ModuleManifest | null> | null {
  const used = useApi(
    (signal) => Promise.all(plans.map((p) => api.getCatalogPlan(p.id, { signal }).then((x) => [p.id, x.fulfilment?.module ?? null] as const, () => [p.id, null] as const))),
    [plans.map((p) => p.id).join('\n')],
  );
  const modules = useModuleSummaries(branch);
  if (used.status !== 'success' || !modules) return null;
  return new Map(used.data.flatMap(([plan, m]) => (m ? [[plan, modules.find((x) => x.manifest.id === m)?.manifest ?? null] as const] : [])));
}

/** "accounts / most" of a server, or of the servers of a member; a most of 0 takes no account (hosting_core::rules): full. */
export function useAccountsText(): (accounts: number, max: number) => string {
  const { t } = useCatalogT();
  return (accounts, max) => (max > 0 ? t('connection.accountsOf', { accounts, max }) : t('connection.accountsFull', { accounts }));
}

/** What one connection reports: when its snapshot was read (`discover`), and for hosting its servers (`hosting.servers`). */
export interface InstanceFacts {
  fetchedAt: string | null;
  snapshotError: boolean;
  servers: HostingServer[] | null;
}

/** The facts of each connection of a group; `refreshed` reloads them after "Làm mới". */
export function useMemberFacts(branch: ModuleBranchId, manifest: ModuleManifest | null, keys: readonly string[], refreshed = 0): Map<string, InstanceFacts> | null {
  const discover = manifest?.admin_actions.some((a) => a.key === 'discover') ?? false;
  const r = useApi(
    (signal) =>
      Promise.all(
        keys.map(async (key): Promise<[string, InstanceFacts]> => {
          const snap = discover && manifest ? await api.runModuleAction(branch, manifest.id, key, 'discover', { input: { refresh: false } }, { signal }).catch(() => null) : null;
          const servers = branch === 'hosting' && manifest ? await api.listHostingServers(manifest.id, key, { signal }).catch(() => null) : null;
          const value = snap?.outcome === 'ok' ? (snap.value as { fetched_at?: string | null } | null) : null;
          return [key, { fetchedAt: value?.fetched_at ?? null, snapshotError: discover && snap?.outcome !== 'ok', servers: servers?.items ?? null }];
        }),
      ).then((rows) => new Map(rows)),
    [branch, manifest?.id, keys.join('\n'), refreshed],
  );
  return r.status === 'success' ? r.data : null;
}

/** The connections of a group's members, once each (accepting ones only with `accepting`). */
export function memberKeys(members: readonly PlacementMember[], module: string | null, accepting: boolean): string[] {
  return [...new Set(members.filter((m) => m.module === module && (!accepting || m.accepting)).map((m) => m.instance_key))];
}

/** The members of a server group with what their connections report (ADR-032 §2.11). */
export function MemberFactsTable({ branch, members, summary, facts }: { branch: ModuleBranchId; members: readonly PlacementMember[]; summary: ModuleSummary | null; facts: Map<string, InstanceFacts> | null }) {
  const { t } = useCatalogT();
  const label = useInstanceLabel();
  const accountsText = useAccountsText();
  const columns: Column<PlacementMember>[] = [
    {
      key: 'instance',
      header: t('connection.instance'),
      cell: (m) => {
        const inst = summary?.instances.find((i) => i.key === m.instance_key);
        return (
          <Link to={modulesUrl(branch, m.module, m.instance_key)} className="font-medium text-primary underline-offset-4 hover:underline">
            {inst ? label(inst) : m.instance_key}
            <span className="block font-mono text-xs font-normal text-muted-foreground">{`${m.module}/${m.instance_key}`}</span>
          </Link>
        );
      },
    },
    { key: 'server', header: t('connection.server'), cell: (m) => (m.server ? <span className="font-mono text-xs">{m.server}</span> : <span className="text-xs text-muted-foreground">{t('connection.anyServer')}</span>) },
    { key: 'weight', header: t('servers.weight'), numeric: true, cell: (m) => m.weight },
    {
      key: 'state',
      header: t('connection.state'),
      cell: (m) => {
        const inst = summary?.instances.find((i) => i.key === m.instance_key);
        return (
          <span className="inline-flex flex-wrap items-center gap-1">
            <Badge tone={m.accepting ? 'ok' : 'warn'}>{m.accepting ? t('delivery.accepting') : t('delivery.notAccepting')}</Badge>
            {inst ? <InstanceStatusBadge status={inst.status} /> : null}
          </span>
        );
      },
    },
    {
      key: 'accounts',
      header: t('connection.accounts'),
      cell: (m) => {
        // A server the panel no longer reports (`gone_at`) takes no account: left out.
        const servers = facts?.get(m.instance_key)?.servers?.filter((s) => !s.gone_at && (!m.server || s.server === m.server));
        if (branch !== 'hosting') return '—';
        if (!servers?.length) return <span className="text-xs text-muted-foreground">{t('connection.noFacts')}</span>;
        const sum = (k: 'accounts' | 'max_accounts' | 'pending') => servers.reduce((n, s) => n + s[k], 0);
        return (
          <span className="tabular">
            {accountsText(sum('accounts') + sum('pending'), sum('max_accounts'))}
            {servers.some((s) => !s.up) ? <span className="block text-xs text-critical">{t('connection.serverDown')}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'facts',
      header: t('connection.facts'),
      cell: (m) => {
        const f = facts?.get(m.instance_key);
        const checked = f?.servers?.find((s) => !m.server || s.server === m.server)?.checked_at ?? null;
        const at = f?.fetchedAt ?? checked;
        if (at) return <span className="text-xs">{t('connection.factsAge', { age: formatRelative(at) })}</span>;
        return <span className="text-xs text-muted-foreground">{f?.snapshotError ? t('connection.factsError') : t('connection.noFacts')}</span>;
      },
    },
  ];
  return <DataTable columns={columns} rows={[...members]} rowKey={(m) => `${m.module}/${m.instance_key}/${m.server}`} />;
}

/** "Làm mới" (ADR-032 §2.6): every connection reads its server again (`discover`, refresh) and keeps the new snapshot. */
export function RefreshFactsButton({ branch, module, keys, onDone }: { branch: ModuleBranchId; module: string; keys: readonly string[]; onDone: () => void }) {
  const { t } = useCatalogT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const run = async () => {
    setBusy(true);
    setError(null);
    const failed = await Promise.all(
      keys.map((key) =>
        api.runModuleAction(branch, module, key, 'discover', { input: { refresh: true } }).then(
          (r) => (r.outcome === 'ok' ? null : `${key}: ${r.refusal?.message ?? r.failure?.detail ?? r.outcome}`),
          (e: unknown) => `${key}: ${e instanceof Error ? e.message : String(e)}`,
        ),
      ),
    );
    const lines = failed.filter((x): x is string => x !== null);
    if (lines.length) setError(new Error(lines.join('; ')));
    setBusy(false);
    onDone();
  };
  return (
    <span className="grid gap-1">
      <Button type="button" size="sm" variant="outline" disabled={busy || keys.length === 0} onClick={() => void run()}>
        <RefreshCw aria-hidden="true" />
        {t('connection.refresh')}
      </Button>
      {error ? <WriteError error={error} /> : null}
    </span>
  );
}

/**
 * A value read from the servers (ADR-032 §2.6): the list action runs on every accepting member,
 * and only a value every member has can be picked; the others are greyed with the members that
 * lack them and the age of their snapshot. A stored value no member lists stays shown, marked.
 */
export function MemberListSelect({
  branch,
  module,
  keys,
  action,
  value,
  onChange,
  empty,
  facts,
}: {
  branch: ModuleBranchId;
  module: string;
  keys: readonly string[];
  action: string;
  value: string;
  onChange: (v: string) => void;
  empty: string;
  facts: Map<string, InstanceFacts> | null;
}) {
  const { t } = useCatalogT();
  const text = useText();
  const r = useApi(
    (signal) =>
      Promise.all(
        keys.map((key) =>
          api.runModuleAction(branch, module, key, action, { input: {} }, { signal }).then(
            (x) => ({ key, choices: x.outcome === 'ok' ? choicesOf(x.value) : null }),
            () => ({ key, choices: null }),
          ),
        ),
      ),
    [branch, module, action, keys.join('\n')],
  );
  const list = r.status === 'success' ? commonChoices(r.data) : [];
  const where = (key: string) => {
    const at = facts?.get(key)?.fetchedAt;
    return at ? `${key} (${formatRelative(at)})` : key;
  };
  return (
    <>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} disabled={r.status === 'loading'}>
        <option value="">{r.status === 'loading' ? '…' : empty}</option>
        {value && !list.some((c) => c.value === value) ? <option value={value}>{t('connection.notListed', { value })}</option> : null}
        {list.map((c) => (
          <option key={c.value} value={c.value} disabled={c.missing.length > 0}>
            {c.missing.length ? `${text(c.label)} · ${t('connection.missingOn', { servers: c.missing.map(where).join(', ') })}` : text(c.label)}
          </option>
        ))}
      </select>
      {r.status !== 'loading' && list.length === 0 ? <span className="text-xs text-muted-foreground">{t('connection.noList')}</span> : null}
    </>
  );
}

/** The form's values: a group's stored settings (else the module's defaults), or only a plan's overrides (`override`). */
export function placementValues(fields: readonly ModuleField[], stored: Readonly<Record<string, unknown>>, override: boolean): Record<string, FormValue> {
  return Object.fromEntries(
    fields
      .filter((f) => !isWriteOnly(f.kind))
      .map((f): [string, FormValue] => {
        if (!override) return [f.key, initialValue(f, { values: { ...stored } })];
        const v = stored[f.key];
        if (v === undefined || v === null) return [f.key, f.kind.type === 'multi_select' ? [] : ''];
        if (f.kind.type === 'boolean') return [f.key, String(v === true)];
        return [f.key, Array.isArray(v) ? v.map(String) : String(v)];
      }),
  );
}

/**
 * What is sent (ADR-032 §2.5): on a group every field (blank: null, the module's default fills it);
 * on a plan only the overrides typed, so an empty field keeps following the group. `valid`: no
 * value the form refuses (the server checks the merged settings again).
 */
export function placementPayload(fields: readonly ModuleField[], values: Readonly<Record<string, FormValue>>, override: boolean): { payload: Record<string, unknown>; valid: boolean } {
  const payload: Record<string, unknown> = {};
  let valid = true;
  for (const f of fields.filter((x) => !isWriteOnly(x.kind) && (!override || x.overridable))) {
    const v = values[f.key] ?? '';
    if (override && (isBlank(v) || (Array.isArray(v) && v.length === 0))) continue;
    if (checkValue(f, v, false)) valid = false;
    // ponytail: a required select listed from the servers (`options_from`) with no default cannot be
    // filled on a group without members (nothing lists it), so such a group cannot be saved; let it
    // save blank and check the field when members are set if a module declares one.
    if (!override && isBlank(v) && f.default === null && isRequired(f, true, values, fields)) valid = false;
    payload[f.key] = override && f.kind.type === 'boolean' ? v === 'true' : payloadValue(f, v);
  }
  return { payload, valid };
}

/**
 * The placement settings of a module (ADR-032 §2.5) with the controls of the module form. On a
 * server group (`defaults` absent) each field is the group's value. On a plan an empty field
 * follows the group, whose value is its placeholder, and a field the plan may not override only
 * shows the group's value. A select listed from the servers reads every accepting member.
 */
export function PlacementFields({
  fields,
  values,
  onChange,
  defaults,
  list,
}: {
  fields: readonly ModuleField[];
  values: Readonly<Record<string, FormValue>>;
  onChange: (key: string, v: FormValue) => void;
  defaults?: Readonly<Record<string, unknown>>;
  list: { branch: ModuleBranchId; module: string; keys: readonly string[]; facts: Map<string, InstanceFacts> | null };
}) {
  const { t } = useCatalogT();
  const text = useText();
  const valueText = useValueText();
  const override = defaults !== undefined;
  const groupValue = (f: ModuleField) => {
    const v = defaults?.[f.key] ?? f.default;
    if (v === undefined || v === null || v === '') return '';
    if (f.kind.type === 'boolean') return v === true ? t('connection.yes') : t('connection.no');
    return Array.isArray(v) ? v.join(', ') : String(v);
  };
  const fromGroup = (f: ModuleField) => (groupValue(f) ? t('connection.fromGroupValue', { value: groupValue(f) }) : t('connection.fromGroup'));
  return (
    <div className="grid items-start gap-3 sm:grid-cols-2">
      {fields
        .filter((f) => !isWriteOnly(f.kind))
        .map((f) => {
          const v = values[f.key] ?? '';
          const hint = f.help ? text(f.help) : undefined;
          if (override && !f.overridable) {
            return (
              <Field key={f.key} label={text(f.label)} hint={t('connection.notOverridable')}>
                <span className="text-sm">{valueText(f, defaults?.[f.key])}</span>
              </Field>
            );
          }
          if (f.kind.type === 'select' && f.kind.options_from) {
            return (
              <Field key={f.key} label={text(f.label)} hint={hint}>
                <MemberListSelect {...list} action={f.kind.options_from} value={typeof v === 'string' ? v : ''} onChange={(x) => onChange(f.key, x)} empty={override ? fromGroup(f) : t('connection.choose')} />
              </Field>
            );
          }
          if (override && f.kind.type === 'boolean') {
            return (
              <Field key={f.key} label={text(f.label)} hint={hint}>
                <select className={selectClass} value={typeof v === 'string' ? v : ''} onChange={(e) => onChange(f.key, e.target.value)}>
                  <option value="">{fromGroup(f)}</option>
                  <option value="true">{t('connection.yes')}</option>
                  <option value="false">{t('connection.no')}</option>
                </select>
              </Field>
            );
          }
          return (
            <div key={f.key} className={f.kind.type === 'boolean' || f.kind.type === 'multi_select' ? 'sm:col-span-2' : undefined}>
              <FieldInput
                field={f}
                value={v}
                onChange={(x) => onChange(f.key, x)}
                required={!override && isRequired(f, true, values, fields)}
                error={isBlank(v) ? null : checkValue(f, v, false)}
                placeholder={override ? fromGroup(f) : undefined}
              />
            </div>
          );
        })}
    </div>
  );
}
